import Vue  from 'vue'
import Vuex from 'vuex'
Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    token:    localStorage.getItem('jxcct_token') || '',
    userInfo: JSON.parse(localStorage.getItem('jxcct_user') || 'null'),
    settings: JSON.parse(localStorage.getItem('jxcct_settings') || 'null'),
    tenantBaseline: null, // 平台为当前租户设置的权限基线（上限层），null 表示不限制
  },
  getters: {
    token:        state => state.token,
    userInfo:     state => state.userInfo,
    tenantBaseline: state => state.tenantBaseline,
    role:         state => state.userInfo ? state.userInfo.role : null,
    settings:     state => state.settings || {},
    logoUrl:      state => (state.settings && state.settings.logo_url)       || require('@/assets/khlogo.png'),
    orgName:      state => (state.settings && state.settings.org_name)       || '旅行社',
    orgShortName: state => (state.settings && state.settings.org_short_name) || '旅行社管理系统',
    orgContact:   state => (state.settings && state.settings.org_contact)     || '',
    // 获取用户权限
    permissions:  state => {
      if (!state.userInfo) return null
      // 优先使用用户独立权限 permissions，其次是角色权限快照 perm_snapshot
      const raw = state.userInfo.permissions || state.userInfo.perm_snapshot || null
      if (!raw || typeof raw !== 'object') return raw
      // 向后兼容：旧数据「线路类型」存放于 product 模块，现归属 resource 模块
      const perms = JSON.parse(JSON.stringify(raw))
      ;['view', 'op'].forEach(type => {
        const pv = (perms.product && Array.isArray(perms.product[type])) ? perms.product[type] : []
        if (pv.includes('线路类型')) {
          if (!perms.resource) perms.resource = { view: [], op: [] }
          if (!Array.isArray(perms.resource[type])) perms.resource[type] = []
          if (!perms.resource[type].includes('线路类型')) perms.resource[type].push('线路类型')
        }
      })
      return perms
    },
    // 检查是否有指定模块的查看权限
    hasViewPerm: (state, getters) => (module, page) => {
      const perms = getters.permissions
      // 平台超管 / 导游：绕过平台权限基线（上限层），拥有全部查看权限
      if (state.userInfo && (state.userInfo.is_super || state.userInfo.role === 'guide')) {
        return true
      }
      // 租户内自身权限：其余需用户自身具备该查看权限
      let own = false
      if (perms && perms[module] && (perms[module].view || []).includes(page)) {
        own = true
      }
      if (!own) return false
      // 叠加平台权限基线（上限层）：基线为 null 表示不限制；否则需基线也允许。
      // 该上限为硬性封顶，即使租户超管也不得超过平台设置。
      const base = state.tenantBaseline
      if (!base) return true
      const baseView = (base[module] && base[module].view) || []
      return baseView.includes(page)
    },
    // 检查是否有指定模块的操作权限
    hasOpPerm: (state, getters) => (module, page) => {
      const perms = getters.permissions
      // 平台超管 / 导游：绕过平台权限基线（上限层），拥有全部操作权限
      if (state.userInfo && (state.userInfo.is_super || state.userInfo.role === 'guide')) {
        return true
      }
      // 租户内自身权限：其余需用户自身具备该操作权限
      let own = false
      if (perms && perms[module] && (perms[module].op || []).includes(page)) {
        own = true
      }
      if (!own) return false
      // 叠加平台权限基线（上限层）：基线为 null 表示不限制；否则需基线也允许。
      // 该上限为硬性封顶，即使租户超管也不得超过平台设置。
      const base = state.tenantBaseline
      if (!base) return true
      const baseOp = (base[module] && base[module].op) || []
      return baseOp.includes(page)
    },
    // 线路类型业务权限：统一返回「无限制」，撤销按用户/角色设置线路类型权限的功能
    // （配置入口 tab 已删除，此处熔断后各中心 filterRouteTypes 自动全量，不再收窄）
    routeTypePerm: () => ({ scope: '', ids: [] }),
  },

  mutations: {
    SET_TOKEN(state, token) {
      state.token = token
      localStorage.setItem('jxcct_token', token)
    },
    SET_USER(state, user) {
      state.userInfo = user
      localStorage.setItem('jxcct_user', JSON.stringify(user))
    },
    SET_TENANT_BASELINE(state, baseline) {
      state.tenantBaseline = baseline
    },
    CLEAR_AUTH(state) {
      state.token    = ''
      state.userInfo = null
      localStorage.removeItem('jxcct_token')
      localStorage.removeItem('jxcct_user')
    },
    SET_SETTINGS(state, settings) {
      state.settings = settings
      localStorage.setItem('jxcct_settings', JSON.stringify(settings))
    }
  },
  actions: {
    login({ commit, dispatch }, { token, userInfo }) {
      commit('SET_TOKEN', token)
      commit('SET_USER', userInfo)
      // 登录后加载平台权限基线（上限层），用于控制左侧菜单可见性；
      // 基线加载失败不影响登录（catch 兜底为 null，仅菜单兜底显示，MainLayout 也会再次兜底）
      dispatch('loadTenantBaseline')
    },
    // 加载平台为当前租户设置的权限基线（上限层）
    async loadTenantBaseline({ commit }) {
      try {
        const request = require('@/utils/request').default
        const res = await request.get('/permission-baseline')
        let baseline = (res && res.data && res.data.baseline) || null
        // 向后兼容：旧基线将「线路类型」存放于 product 模块，现归属 resource 模块
        if (baseline && typeof baseline === 'object') {
          baseline = JSON.parse(JSON.stringify(baseline))
          ;['view', 'op'].forEach(type => {
            const pv = (baseline.product && Array.isArray(baseline.product[type])) ? baseline.product[type] : []
            if (pv.includes('线路类型')) {
              if (!baseline.resource) baseline.resource = { view: [], op: [] }
              if (!Array.isArray(baseline.resource[type])) baseline.resource[type] = []
              if (!baseline.resource[type].includes('线路类型')) baseline.resource[type].push('线路类型')
            }
          })
        }
        commit('SET_TENANT_BASELINE', baseline)
      } catch (e) {
        commit('SET_TENANT_BASELINE', null)
      }
    },
    logout({ commit }) {
      commit('CLEAR_AUTH')
    },
    async fetchSettings({ commit }) {
      try {
        const request = require('@/utils/request').default
        const res = await request.get('/settings', { _silent: true })
        if (res && res.data) {
          commit('SET_SETTINGS', res.data)
        }
      } catch (e) { /* 忽略，使用默认值 */ }
    }
  }
})
